import { CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTable, useSortBy, useRowSelect, Hooks, useResizeColumns, Column } from 'react-table';

import { Visible } from 'coreOld/components';
import { Loader } from 'coreOld/elements';
import { AlignText } from 'coreOld/enums';

import { Body } from './Body';
import { Header } from './Header';
import {
  infiniteScrollStyle,
  NoRecordsFound,
  Table,
  TableBody,
  TableHeight,
  TableWrap,
  WrapperTable,
} from './importTableStyles';

interface IProps {
  data;
  columns: Column<object>[];
  name: string;
  borderBottom?: boolean;
  borderRight?: boolean;
  headerLines?: number;
  footerLines?: number;
  alternating?: boolean;
  hasRowSelection?: boolean;
  scrollableTarget?: string;
  hasFooter?: boolean;
  hasFilters?: boolean;
  showInfoIcon?: boolean;
  isRowEditable?: boolean;
  onCheckboxClick?: () => void;
  isModalOpen?: boolean;
  renderEditButtons?: (hooks: Hooks<object>, isRowEditable: boolean) => void;
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  tableBodyStyle?: CSSProperties;
  textAlign?: AlignText;
  defaultColumnStyle?;
  onRowClick?;
  setSelectedRows?;
  fetchNextPage?;
}

const ImportTableView = ({
  name,
  columns,
  data,
  borderBottom = false,
  borderRight = false,
  alternating = true,
  hasRowSelection = false,
  headerLines = 1,
  scrollableTarget = name,
  onRowClick,
  isLoading = false,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  defaultColumnStyle,
  tableBodyStyle,
  textAlign = AlignText.RIGHT,
}: IProps) => {
  const { t } = useTranslation();

  const defaultColumn = useMemo(
    () => ({
      minWidth: 30,
      width: 150,
      maxWidth: 200,
      ...defaultColumnStyle,
    }),
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useSortBy,
    useRowSelect,
    useResizeColumns
  );

  return (
    <TableHeight id={scrollableTarget}>
      <Visible when={isLoading}>
        <Loader fullHeight={true} />
      </Visible>

      <InfiniteScroll
        dataLength={rows.length}
        next={fetchNextPage}
        hasMore={hasNextPage}
        scrollableTarget={scrollableTarget}
        loader={isFetchingNextPage && <Loader />}
        style={infiniteScrollStyle}
      >
        <WrapperTable>
          <TableWrap>
            <Table {...getTableProps()}>
              <Header
                headerGroups={headerGroups}
                borderBottom={borderBottom}
                lines={headerLines}
                hasRowSelection={hasRowSelection}
                textAlign={textAlign}
              />

              <TableBody {...getTableBodyProps()}>
                <Body
                  rows={rows}
                  prepareRow={prepareRow}
                  borderRight={borderRight}
                  borderBottom={borderBottom}
                  alternating={alternating}
                  onRowClick={onRowClick}
                  tableBodyStyle={tableBodyStyle}
                  textAlign={textAlign}
                />
              </TableBody>
            </Table>
          </TableWrap>

          <Visible when={rows.length === 0 && !isLoading}>
            <NoRecordsFound>{t('messageNoItemsFound')}</NoRecordsFound>
          </Visible>
        </WrapperTable>
      </InfiniteScroll>
    </TableHeight>
  );
};

export default ImportTableView;
